import './input.scss';

import {
  type InputHTMLAttributes,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react';

import AlertCircleIcon from '@/media/icons/alert-circle.svg?react';
import { checkPercentageValue } from '@/shared/services/helpers/strings';

export type IInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  error?: string;
  endContent?: React.ReactNode;
  isRequired?: boolean;
  classes?: {
    root?: string;
    label?: string;
    error?: string;
  };
  fileName?: string;
  isFrozen?: boolean;
  errorWithoutMessage?: boolean;
  isPercentage?: boolean;
};

const Input = forwardRef<HTMLInputElement, IInputProps>(
  (
    {
      label,
      classes,
      error,
      isRequired,
      endContent,
      fileName,
      isFrozen,
      errorWithoutMessage,
      isPercentage,
      ...rest
    },
    forwardedRef
  ) => {
    const endContainerRef = useRef<HTMLDivElement>(null);
    const ref = useRef<HTMLInputElement>(null);
    useImperativeHandle(forwardedRef, () => ref.current as HTMLInputElement);

    useEffect(() => {
      // Увеличиваем размер шрифта для safari, чтобы нормально отображались поля
      if (
        /^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
        ref.current
      ) {
        ref.current.style.fontSize = '16px';
      }
    }, []);

    const isInputFile = rest.type === 'file';

    const rootClass = ['input', classes?.root ? classes?.root : '']
      .join(' ')
      .trim();
    const wrapperClass = [
      'input__wrapper',
      isInputFile ? 'input__wrapper--with-file' : '',
      isInputFile && error !== undefined ? 'input__field--error' : '',
      !label ? 'input__wrapper--without-label' : ''
    ]
      .join(' ')
      .trim();
    const labelClass = [
      'input__label',
      classes?.label ? classes.label : '',
      rest.value || fileName ? 'input__label--focused' : ''
    ]
      .join(' ')
      .trim();
    const errorClass = classes?.error ? classes.error : '';
    const fieldClass = [
      'input__field',
      rest.className ? rest.className : '',
      error !== undefined ? 'input__field--error' : '',
      !label ? 'input__field--without-label' : '',
      isInputFile ? 'input__field--file' : '',
      isFrozen ? 'input__field--frozen' : ''
    ]
      .join(' ')
      .trim();

    // eslint-disable-next-line react-hooks/refs
    const inputPadding = endContainerRef.current
      ? // eslint-disable-next-line react-hooks/refs
        `${endContainerRef.current.getBoundingClientRect().width || 44}px`
      : undefined;

    return (
      <div className={rootClass}>
        <label className={wrapperClass} htmlFor={rest.id}>
          <input
            readOnly={isFrozen}
            inputMode={isFrozen ? 'none' : undefined}
            {...rest}
            ref={ref}
            type={isFrozen && isInputFile ? 'text' : rest.type}
            style={{ paddingRight: inputPadding, ...rest.style }}
            placeholder={!label || rest.placeholder ? rest.placeholder : ''}
            value={rest.value}
            className={fieldClass}
            onChange={
              isFrozen
                ? () => null
                : (e) => {
                    if (isPercentage && !checkPercentageValue(e.target.value))
                      return;
                    rest.onChange?.(e);
                  }
            }
            onBlur={(e) => {
              rest.onBlur?.(e);
              if (isPercentage && e.target.value.endsWith('.')) {
                rest.onChange?.({
                  ...e,
                  target: {
                    ...e.target,
                    value: e.target.value.slice(0, -1)
                  }
                });
              }
            }}
          />
          {isInputFile && (
            <span
              className="input__file-name"
              style={{
                opacity: fileName ? '1' : '0',
                width: `calc(100% - ${inputPadding})`
              }}
            >
              {fileName ? fileName : 'Нет файла'}
            </span>
          )}
          {label && (
            <p
              style={{ width: endContent ? '75%' : undefined }}
              className={labelClass}
            >
              {label}
              {isRequired && <span className="input__require">*</span>}
            </p>
          )}
          {endContent && (
            <div ref={endContainerRef} className="input__end-container">
              {endContent}
            </div>
          )}
        </label>
        {error && !errorWithoutMessage && (
          <p
            className={`input__error ${errorClass}`}
            onClick={() => ref.current?.focus()}
          >
            <AlertCircleIcon />
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
